import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Buildings, UsersThree } from "@phosphor-icons/react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import MetricsPanel from "../components/MetricsPanel";
import TicketsPanel from "../components/TicketsPanel";
import SatisfactionStrip from "../components/SatisfactionStrip";
import { ROLE_LABELS, initials } from "../lib/utils";

function PeopleColumn({ title, people, empty, linkNames }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold mb-2">{title}</div>
      <div className="space-y-2">
        {people.map((p) => (
          <div key={p.id} className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[10px] font-semibold">{initials(p.name)}</div>
            <div className="min-w-0">
              {linkNames ? (
                <Link to={`/users/${p.id}`} className="text-sm text-slate-800 font-medium hover:text-blue-600 hover:underline block truncate">{p.name}</Link>
              ) : (
                <div className="text-sm text-slate-800 font-medium truncate">{p.name}</div>
              )}
              <div className="text-[11px] text-slate-400">{ROLE_LABELS[p.role] || p.role}</div>
            </div>
          </div>
        ))}
        {people.length === 0 && <div className="text-xs text-slate-400">{empty}</div>}
      </div>
    </div>
  );
}

export function PeopleCard({ members, linkNames, footnote }) {
  if (!members) return null;
  return (
    <div className="surface-card p-5" data-testid="people-card">
      <div className="flex items-center gap-2 mb-3">
        <UsersThree size={16} className="text-slate-400" />
        <h3 className="font-display text-base font-semibold text-slate-900">People</h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <PeopleColumn title="Internal team" people={members.team} empty="No team members assigned." linkNames={linkNames} />
        <PeopleColumn title="Client users" people={members.clients} empty="No client users yet." linkNames={linkNames} />
      </div>
      {footnote && <div className="text-[11px] text-slate-400 mt-4 pt-3 border-t border-slate-100">{footnote}</div>}
    </div>
  );
}

export default function ProjectDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [ratings, setRatings] = useState(null);
  const [err, setErr] = useState(null);
  const isManager = ["super_admin", "project_manager"].includes(user.role);

  useEffect(() => {
    setData(null); setRatings(null); setErr(null);
    api.get(`/projects/${id}`).then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.status === 404 ? "Not found or no access." : "Failed to load."));
    if (isManager) api.get("/reports/ratings", { params: { project_id: id } }).then((r) => setRatings(r.data)).catch(() => setRatings({ error: true }));
  }, [id, isManager]);

  if (err) return <div className="surface-card p-8 text-center text-slate-500">{err}</div>;
  if (!data) return <div className="surface-card p-8 text-center text-slate-400">Loading…</div>;

  const p = data.project;
  return (
    <div className="space-y-5" data-testid="project-detail-page">
      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-blue-600 font-semibold">Project</div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 mt-1">{p.name}</h1>
        <div className="flex items-center gap-2 mt-1.5 text-sm text-slate-500 flex-wrap">
          {p.organization_id ? (
            <Link to={`/organizations/${p.organization_id}`} className="inline-flex items-center gap-1.5 hover:text-blue-600 hover:underline">
              <Buildings size={14} /> {data.organization_name}
            </Link>
          ) : <span>{data.organization_name}</span>}
          {p.project_type && (
            <span className="text-xs border border-slate-200 rounded-full px-2 py-0.5 text-slate-500 capitalize">{String(p.project_type).replace(/_/g, " ")}</span>
          )}
        </div>
        {p.description && <p className="text-sm text-slate-500 mt-2 max-w-2xl">{p.description}</p>}
      </div>
      <MetricsPanel metrics={data.metrics} />
      {isManager && <SatisfactionStrip ratings={ratings} />}
      <TicketsPanel scopeParams={{ project_id: id }} testId="project-tickets-panel" />
      <PeopleCard members={data.members} linkNames={isManager}
        footnote="Super admins and project managers have access to every project." />
    </div>
  );
}
